import { useEffect, useState } from "react";
import { Sparkle, FileArrowUp, CheckCircle, UserPlus } from "@phosphor-icons/react";
import { fetchMyActivity } from "@/lib/api";

// Right-side rail on the Dashboard: Velocity Insights card (efficiency dial +
// active-time + overdue) followed by Recent Activity feed. Inputs come from
// /api/my/activity for the feed, and a client-side roll-up across the user's
// projects for the insights numbers.
function ringStyle(pct) {
  const angle = Math.max(0, Math.min(100, pct)) * 3.6;
  return {
    background: `conic-gradient(#5eead4 ${angle}deg, rgba(255,255,255,0.35) ${angle}deg)`,
  };
}

function relTime(iso) {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

const ACTION_ICON = {
  "task.created": FileArrowUp,
  "task.updated": CheckCircle,
  "task.reordered": Sparkle,
  "task.deleted": CheckCircle,
};
const ACTION_TONE = {
  "task.created": "bg-violet-100 text-violet-600",
  "task.updated": "bg-emerald-100 text-emerald-600",
  "task.reordered": "bg-slate-100 text-slate-600",
  "task.deleted": "bg-rose-100 text-rose-600",
};

export default function VelocityInsightsPanel({ efficiency, activeTime, overdueCount }) {
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const items = await fetchMyActivity(12);
        if (!cancel) setActivity(items);
      } catch (_) {
        if (!cancel) setActivity([]);
      }
    })();
    return () => { cancel = true; };
  }, []);

  return (
    <div className="space-y-5" data-testid="velocity-insights-panel">
      {/* Velocity Insights */}
      <section
        className="relative overflow-hidden rounded-2xl p-6 text-slate-900"
        style={{ background: "#a7f3eb" }}
        data-testid="velocity-insights"
      >
        <div className="flex items-start justify-between mb-5">
          <h3 className="text-xl font-display font-bold tracking-tight">Velocity Insights</h3>
          <Sparkle size={18} weight="fill" className="text-slate-700" />
        </div>

        <div className="flex items-center gap-5 mb-6">
          <div
            className="relative h-20 w-20 shrink-0 rounded-full flex items-center justify-center aspect-square"
            style={ringStyle(efficiency)}
            data-testid="insights-efficiency-ring"
          >
            <div className="absolute inset-1.5 rounded-full bg-[#a7f3eb] flex items-center justify-center">
              <span className="text-xl font-display font-bold text-slate-900" data-testid="insights-efficiency">
                {Math.round(efficiency)}%
              </span>
            </div>
          </div>
          <div className="text-sm">
            <div className="text-[10px] uppercase tracking-widest font-semibold font-sf-label text-slate-700">
              Efficiency score
            </div>
            <div className="mt-1 text-slate-700">
              Based on open tasks vs. overdue. Higher is better.
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-900/10">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold font-sf-label text-slate-700 mb-1">
              Active time
            </div>
            <div className="text-2xl font-display font-bold" data-testid="insights-active-time">{activeTime}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold font-sf-label text-slate-700 mb-1">
              Overdue
            </div>
            <div className="text-2xl font-display font-bold" data-testid="insights-overdue">{overdueCount}</div>
          </div>
        </div>
      </section>

      {/* Recent Activity */}
      <section
        className="rounded-2xl border border-slate-200 bg-white p-6"
        data-testid="recent-activity"
      >
        <h3 className="text-xl font-display font-bold tracking-tight mb-4">Recent Activity</h3>
        {activity.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-4">
            No activity yet. As your team edits projects, you'll see it here.
          </div>
        ) : (
          <ul className="space-y-4">
            {activity.slice(0, 6).map((a) => {
              const Icon = ACTION_ICON[a.action] || Sparkle;
              const tone = ACTION_TONE[a.action] || "bg-slate-100 text-slate-600";
              return (
                <li key={a.id} className="flex items-start gap-3" data-testid={`activity-item-${a.id}`}>
                  <span className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${tone}`}>
                    <Icon size={14} weight="bold" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-900 leading-snug">
                      <span className="font-semibold">{a.actor_name}</span>{" "}
                      <span className="text-slate-700">{a.summary}</span>
                    </div>
                    <div className="mt-0.5 text-[10px] uppercase tracking-widest text-slate-400 font-sf-label">
                      {relTime(a.created_at)}{a.project_name ? ` · ${a.project_name}` : ""}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <button
          type="button"
          className="mt-5 w-full h-10 rounded-md border border-slate-200 hover:border-slate-950 text-slate-700 hover:text-slate-950 text-[11px] uppercase tracking-widest font-semibold font-sf-label transition-colors"
          data-testid="activity-view-full-log"
        >
          View full log
        </button>
      </section>
    </div>
  );
}
